import {connect} from 'react-redux';
import {bindActionCreators, type Dispatch} from 'redux';

import {getChannel} from 'mattermost-redux/selectors/entities/channels';
import {
    getPost,
    makeIsPostCommentMention,
    makeGetCommentCountForPost,
    isPostIdSendingForCurrentUser,
} from 'mattermost-redux/selectors/entities/posts';

import {get} from 'mattermost-redux/selectors/entities/preferences';
import {getCurrentUserId} from 'mattermost-redux/selectors/entities/users';

import {type GenericAction} from 'mattermost-redux/types/actions';
import {type Post} from 'mattermost-redux/types/posts';

import {markPostAsUnread} from 'actions/post_actions';
import {selectPost, selectPostCard} from 'actions/views/rhs';

import {type GlobalState} from 'types/store';

import {isArchivedChannel} from 'utils/channel_utils';
import Constants, {Preferences} from 'utils/constants';
import {areConsecutivePostsBySameUser} from 'utils/post_utils';
import {getIsPostBeingEdited, getIsPostBeingEditedInRHS} from '../../../selectors/posts';

import {isFeatureEnabled} from 'utils/utils';

import PostComponent from './post';

interface OwnProps {
    post?: Post;
    postId: string;
    previousPostId?: string;
}

// isFirstReply returns true when the given post a comment that isn't part of the same thread as the previous post.
export function isFirstReply(post: Post, previousPost: Post): boolean {
    if (post.root_id) {
        if (previousPost) {
            // Returns true as long as the previous post is part of a different thread
            return post.root_id !== previousPost.id && post.root_id !== previousPost.root_id;
        }

        // The previous post is not a real post
        return true;
    }

    // This post is not a reply
    return false;
}

function makeMapStateToProps() {
    const getReplyCount = makeGetCommentCountForPost();
    const isPostCommentMention = makeIsPostCommentMention();

    return (state: GlobalState, ownProps: OwnProps) => {
        const post = ownProps.post || getPost(state, ownProps.postId)!;
        const channel = getChannel(state, post.channel_id);
        const newEditorEnabled = isFeatureEnabled(Constants.PRE_RELEASE_FEATURES.NEW_EDITOR, state);

        let previousPost = null;
        if (ownProps.previousPostId) {
            previousPost = getPost(state, ownProps.previousPostId);
        }

        let consecutivePostByUser = false;
        let previousPostIsComment = false;

        if (previousPost) {
            consecutivePostByUser = areConsecutivePostsBySameUser(post, previousPost);
            previousPostIsComment = Boolean(previousPost.root_id);
        }

        return {
            post,
            isBeingEdited: getIsPostBeingEdited(state, post.id) && !getIsPostBeingEditedInRHS(state, post.id) && !newEditorEnabled,
            currentUserId: getCurrentUserId(state),
            isSending: isPostIdSendingForCurrentUser(state, post.pending_post_id),
            isFirstReply: previousPost ? isFirstReply(post, previousPost) : false,
            consecutivePostByUser,
            previousPostIsComment,
            hasReplies: getReplyCount(state, post) > 0,
            isCommentMention: isPostCommentMention(state, post.id),
            center:
                get(
                    state,
                    Preferences.CATEGORY_DISPLAY_SETTINGS,
                    Preferences.CHANNEL_DISPLAY_MODE,
                    Preferences.CHANNEL_DISPLAY_MODE_DEFAULT,
                ) === Preferences.CHANNEL_DISPLAY_MODE_CENTERED,
            compactDisplay:
                get(
                    state,
                    Preferences.CATEGORY_DISPLAY_SETTINGS,
                    Preferences.MESSAGE_DISPLAY,
                    Preferences.MESSAGE_DISPLAY_DEFAULT,
                ) === Preferences.MESSAGE_DISPLAY_COMPACT,
            channelIsArchived: isArchivedChannel(channel),
            channelName: channel?.name,
            channelType: channel?.type,
            isFlagged: get(state, Preferences.CATEGORY_FLAGGED_POST, post.id, null) != null,
            clickToReply:
                get(
                    state,
                    Preferences.CATEGORY_DISPLAY_SETTINGS,
                    Preferences.CLICK_TO_REPLY,
                    Preferences.CLICK_TO_REPLY_DEFAULT,
                ) === 'true',
            newEditorEnabled,
        };
    };
}

function mapDispatchToProps(dispatch: Dispatch<GenericAction>) {
    return {
        actions: bindActionCreators(
            {
                selectPost,
                selectPostCard,
                markPostAsUnread,
            },
            dispatch,
        ),
    };
}

export default connect(makeMapStateToProps, mapDispatchToProps)(PostComponent);
